import { useState } from 'react';
import flags from 'react-phone-number-input/flags';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt } from 'react-icons/fa';
import './ins_afterwork.css'

/* ============================================================
 *  CONFIG ÉVÉNEMENT
 * ============================================================ */
const EVENT = {
    eventName: 'On se retrouve !',
    eventTagline:
        "Une soirée pour se détendre, partager de bons moments et célébrer ensemble les réussites de l’année.",
    eventDate: 'Vendredi 18 décembre 2026',
    eventTime: 'À partir de 18h00',
    eventPlace: 'Espace Omel',
};

/* ============================================================
 *  API
 * ============================================================ */
const API_URL = (process.env.REACT_APP_HOTE || '').replace(/\/$/, '');
const REGISTER_PATH = '/inscriptions-afterwork/';

async function submitRegistration(data) {
    const payload = {
        nom: data.nom,
        prenom: data.prenom,
        institution: data.institution,
        telephone: data.telephone || '',
        certifie: data.certifie ? 'oui' : 'non',
        droit_image: data.droit_image ? 'oui' : 'non',
    };

    if (!payload.telephone) {
        throw new Error('Le numéro de téléphone est obligatoire.');
    }

    let response;
    try {
        response = await fetch(`${API_URL}${REGISTER_PATH}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    } catch {
        throw new Error(
            'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.'
        );
    }

    if (!response.ok) {
        let message = "L'inscription a échoué. Réessayez dans un instant.";
        try {
            const body = await response.json();
            if (typeof body.error === 'string') message = body.error;
            else if (typeof body.detail === 'string') message = body.detail;
            else if (typeof body.message === 'string') message = body.message;
        } catch {
            /* réponse non JSON */
        }
        throw new Error(message);
    }

    try {
        return await response.json();
    } catch {
        return {};
    }
}

/* ============================================================
 *  BACKGROUND FESTIF
 * ============================================================ */
const LIGHTS = [
    { x: 8, y: 12, s: 180, c: 'var(--magenta)', d: 22 },
    { x: 78, y: 8, s: 240, c: 'var(--cyan)', d: 28 },
    { x: 40, y: 30, s: 120, c: 'var(--ambre)', d: 19 },
    { x: 90, y: 45, s: 200, c: 'var(--magenta)', d: 25 },
    { x: 15, y: 60, s: 260, c: 'var(--violet)', d: 30 },
    { x: 60, y: 70, s: 150, c: 'var(--cyan)', d: 21 },
    { x: 30, y: 88, s: 190, c: 'var(--ambre)', d: 26 },
    { x: 85, y: 90, s: 230, c: 'var(--violet)', d: 24 },
];

const CONFETTI_COLORS = ['#ff3d81', '#ffb627', '#2de2e6', '#fff4e6', '#a66bff'];
const CONFETTI = Array.from({ length: 36 }, (_, i) => ({
    x: (i * 53) % 100,
    y: (i * 37) % 100,
    r: (i * 47) % 360,
    w: 6 + ((i * 7) % 8),
    h: 10 + ((i * 11) % 10),
    c: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    round: i % 4 === 0,
}));

function DiscoBall() {
    return (
        <div className="disco" aria-hidden="true">
            <div className="disco__string" />
            <svg viewBox="0 0 120 120" className="disco__ball">
                <defs>
                    <radialGradient id="ballShade" cx="35%" cy="30%" r="75%">
                        <stop offset="0%" stopColor="#ffffff" />
                        <stop offset="45%" stopColor="#b9b3d6" />
                        <stop offset="100%" stopColor="#2a1650" />
                    </radialGradient>
                    <pattern id="facets" width="12" height="12" patternUnits="userSpaceOnUse">
                        <rect
                            width="12"
                            height="12"
                            fill="none"
                            stroke="rgba(27,10,51,.55)"
                            strokeWidth="1.2"
                        />
                    </pattern>
                    <clipPath id="ballClip">
                        <circle cx="60" cy="60" r="56" />
                    </clipPath>
                </defs>
                <circle cx="60" cy="60" r="56" fill="url(#ballShade)" />
                <g clipPath="url(#ballClip)">
                    <rect width="120" height="120" fill="url(#facets)" />
                    <ellipse cx="60" cy="60" rx="22" ry="56" fill="none" stroke="rgba(27,10,51,.45)" />
                    <ellipse cx="60" cy="60" rx="42" ry="56" fill="none" stroke="rgba(27,10,51,.45)" />
                    <ellipse cx="42" cy="38" rx="14" ry="9" fill="rgba(255,255,255,.65)" />
                </g>
            </svg>
        </div>
    );
}

function Background() {
    return (
        <div className="bg" aria-hidden="true">
            <div className="bg__veil" />

            {LIGHTS.map((l, i) => (
                <span
                    key={i}
                    className="bg__light"
                    style={{
                        left: `${l.x}%`,
                        top: `${l.y}%`,
                        width: l.s,
                        height: l.s,
                        background: l.c,
                        animationDuration: `${l.d}s`,
                        animationDelay: `${-i * 3}s`,
                    }}
                />
            ))}

            {CONFETTI.map((p, i) => (
                <span
                    key={i}
                    className="bg__confetti"
                    style={{
                        left: `${p.x}%`,
                        top: `${p.y}%`,
                        width: p.w,
                        height: p.round ? p.w : p.h,
                        background: p.c,
                        borderRadius: p.round ? '50%' : 2,
                        transform: `rotate(${p.r}deg)`,
                    }}
                />
            ))}

            <DiscoBall />
        </div>
    );
}

/* ============================================================
 *  SUCCESS
 * ============================================================ */
function SuccessMessage({ prenom, onReset }) {
    return (
        <div className="success">
            <div className="success__emoji" aria-hidden="true">🎉</div>
            <h2 className="success__title">C’est noté, {prenom} !</h2>
            <p className="success__text">
                Votre inscription est bien enregistrée. On vous attend pour une belle soirée.
            </p>
            <button type="button" className="btn btn--ghost" onClick={onReset}>
                Inscrire une autre personne
            </button>
        </div>
    );
}

/* ============================================================
 *  TÉLÉPHONE : HELPERS
 * ============================================================ */
const COUNTRIES = [
    { code: 'AF', dial: '93', name: 'Afghanistan' },
    { code: 'ZA', dial: '27', name: 'Afrique du Sud' },
    { code: 'AL', dial: '355', name: 'Albanie' },
    { code: 'DZ', dial: '213', name: 'Algérie' },
    { code: 'DE', dial: '49', name: 'Allemagne' },
    { code: 'AD', dial: '376', name: 'Andorre' },
    { code: 'AO', dial: '244', name: 'Angola' },
    { code: 'AG', dial: '1268', name: 'Antigua-et-Barbuda' },
    { code: 'SA', dial: '966', name: 'Arabie saoudite' },
    { code: 'AR', dial: '54', name: 'Argentine' },
    { code: 'AM', dial: '374', name: 'Arménie' },
    { code: 'AU', dial: '61', name: 'Australie' },
    { code: 'AT', dial: '43', name: 'Autriche' },
    { code: 'AZ', dial: '994', name: 'Azerbaïdjan' },
    { code: 'BS', dial: '1242', name: 'Bahamas' },
    { code: 'BH', dial: '973', name: 'Bahreïn' },
    { code: 'BD', dial: '880', name: 'Bangladesh' },
    { code: 'BB', dial: '1246', name: 'Barbade' },
    { code: 'BE', dial: '32', name: 'Belgique' },
    { code: 'BZ', dial: '501', name: 'Belize' },
    { code: 'BJ', dial: '229', name: 'Bénin' },
    { code: 'BT', dial: '975', name: 'Bhoutan' },
    { code: 'BY', dial: '375', name: 'Biélorussie' },
    { code: 'MM', dial: '95', name: 'Birmanie (Myanmar)' },
    { code: 'BO', dial: '591', name: 'Bolivie' },
    { code: 'BA', dial: '387', name: 'Bosnie-Herzégovine' },
    { code: 'BW', dial: '267', name: 'Botswana' },
    { code: 'BR', dial: '55', name: 'Brésil' },
    { code: 'BN', dial: '673', name: 'Brunei' },
    { code: 'BG', dial: '359', name: 'Bulgarie' },
    { code: 'BF', dial: '226', name: 'Burkina Faso' },
    { code: 'BI', dial: '257', name: 'Burundi' },
    { code: 'KH', dial: '855', name: 'Cambodge' },
    { code: 'CM', dial: '237', name: 'Cameroun' },
    { code: 'CA', dial: '1', name: 'Canada' },
    { code: 'CV', dial: '238', name: 'Cap-Vert' },
    { code: 'CF', dial: '236', name: 'Centrafrique' },
    { code: 'CL', dial: '56', name: 'Chili' },
    { code: 'CN', dial: '86', name: 'Chine' },
    { code: 'CY', dial: '357', name: 'Chypre' },
    { code: 'CO', dial: '57', name: 'Colombie' },
    { code: 'KM', dial: '269', name: 'Comores' },
    { code: 'CG', dial: '242', name: 'Congo' },
    { code: 'CD', dial: '243', name: 'RD Congo' },
    { code: 'KP', dial: '850', name: 'Corée du Nord' },
    { code: 'KR', dial: '82', name: 'Corée du Sud' },
    { code: 'CR', dial: '506', name: 'Costa Rica' },
    { code: 'CI', dial: '225', name: "Côte d'Ivoire" },
    { code: 'HR', dial: '385', name: 'Croatie' },
    { code: 'CU', dial: '53', name: 'Cuba' },
    { code: 'DK', dial: '45', name: 'Danemark' },
    { code: 'DJ', dial: '253', name: 'Djibouti' },
    { code: 'DM', dial: '1767', name: 'Dominique' },
    { code: 'EG', dial: '20', name: 'Égypte' },
    { code: 'AE', dial: '971', name: 'Émirats arabes unis' },
    { code: 'EC', dial: '593', name: 'Équateur' },
    { code: 'ER', dial: '291', name: 'Érythrée' },
    { code: 'ES', dial: '34', name: 'Espagne' },
    { code: 'EE', dial: '372', name: 'Estonie' },
    { code: 'SZ', dial: '268', name: 'Eswatini' },
    { code: 'US', dial: '1', name: 'États-Unis' },
    { code: 'ET', dial: '251', name: 'Éthiopie' },
    { code: 'FJ', dial: '679', name: 'Fidji' },
    { code: 'FI', dial: '358', name: 'Finlande' },
    { code: 'FR', dial: '33', name: 'France' },
    { code: 'GA', dial: '241', name: 'Gabon' },
    { code: 'GM', dial: '220', name: 'Gambie' },
    { code: 'GE', dial: '995', name: 'Géorgie' },
    { code: 'GH', dial: '233', name: 'Ghana' },
    { code: 'GR', dial: '30', name: 'Grèce' },
    { code: 'GD', dial: '1473', name: 'Grenade' },
    { code: 'GT', dial: '502', name: 'Guatemala' },
    { code: 'GN', dial: '224', name: 'Guinée' },
    { code: 'GQ', dial: '240', name: 'Guinée équatoriale' },
    { code: 'GW', dial: '245', name: 'Guinée-Bissau' },
    { code: 'GY', dial: '592', name: 'Guyana' },
    { code: 'HT', dial: '509', name: 'Haïti' },
    { code: 'HN', dial: '504', name: 'Honduras' },
    { code: 'HU', dial: '36', name: 'Hongrie' },
    { code: 'IN', dial: '91', name: 'Inde' },
    { code: 'ID', dial: '62', name: 'Indonésie' },
    { code: 'IQ', dial: '964', name: 'Irak' },
    { code: 'IR', dial: '98', name: 'Iran' },
    { code: 'IE', dial: '353', name: 'Irlande' },
    { code: 'IS', dial: '354', name: 'Islande' },
    { code: 'IL', dial: '972', name: 'Israël' },
    { code: 'IT', dial: '39', name: 'Italie' },
    { code: 'JM', dial: '1876', name: 'Jamaïque' },
    { code: 'JP', dial: '81', name: 'Japon' },
    { code: 'JO', dial: '962', name: 'Jordanie' },
    { code: 'KZ', dial: '7', name: 'Kazakhstan' },
    { code: 'KE', dial: '254', name: 'Kenya' },
    { code: 'KG', dial: '996', name: 'Kirghizistan' },
    { code: 'KI', dial: '686', name: 'Kiribati' },
    { code: 'KW', dial: '965', name: 'Koweït' },
    { code: 'LA', dial: '856', name: 'Laos' },
    { code: 'LS', dial: '266', name: 'Lesotho' },
    { code: 'LV', dial: '371', name: 'Lettonie' },
    { code: 'LB', dial: '961', name: 'Liban' },
    { code: 'LR', dial: '231', name: 'Liberia' },
    { code: 'LY', dial: '218', name: 'Libye' },
    { code: 'LI', dial: '423', name: 'Liechtenstein' },
    { code: 'LT', dial: '370', name: 'Lituanie' },
    { code: 'LU', dial: '352', name: 'Luxembourg' },
    { code: 'MK', dial: '389', name: 'Macédoine du Nord' },
    { code: 'MG', dial: '261', name: 'Madagascar' },
    { code: 'MY', dial: '60', name: 'Malaisie' },
    { code: 'MW', dial: '265', name: 'Malawi' },
    { code: 'MV', dial: '960', name: 'Maldives' },
    { code: 'ML', dial: '223', name: 'Mali' },
    { code: 'MT', dial: '356', name: 'Malte' },
    { code: 'MA', dial: '212', name: 'Maroc' },
    { code: 'MH', dial: '692', name: 'Îles Marshall' },
    { code: 'MU', dial: '230', name: 'Maurice' },
    { code: 'MR', dial: '222', name: 'Mauritanie' },
    { code: 'MX', dial: '52', name: 'Mexique' },
    { code: 'FM', dial: '691', name: 'Micronésie' },
    { code: 'MD', dial: '373', name: 'Moldavie' },
    { code: 'MC', dial: '377', name: 'Monaco' },
    { code: 'MN', dial: '976', name: 'Mongolie' },
    { code: 'ME', dial: '382', name: 'Monténégro' },
    { code: 'MZ', dial: '258', name: 'Mozambique' },
    { code: 'NA', dial: '264', name: 'Namibie' },
    { code: 'NR', dial: '674', name: 'Nauru' },
    { code: 'NP', dial: '977', name: 'Népal' },
    { code: 'NI', dial: '505', name: 'Nicaragua' },
    { code: 'NE', dial: '227', name: 'Niger' },
    { code: 'NG', dial: '234', name: 'Nigéria' },
    { code: 'NO', dial: '47', name: 'Norvège' },
    { code: 'NZ', dial: '64', name: 'Nouvelle-Zélande' },
    { code: 'OM', dial: '968', name: 'Oman' },
    { code: 'UG', dial: '256', name: 'Ouganda' },
    { code: 'UZ', dial: '998', name: 'Ouzbékistan' },
    { code: 'PK', dial: '92', name: 'Pakistan' },
    { code: 'PW', dial: '680', name: 'Palaos' },
    { code: 'PS', dial: '970', name: 'Palestine' },
    { code: 'PA', dial: '507', name: 'Panama' },
    { code: 'PG', dial: '675', name: 'Papouasie-Nouvelle-Guinée' },
    { code: 'PY', dial: '595', name: 'Paraguay' },
    { code: 'NL', dial: '31', name: 'Pays-Bas' },
    { code: 'PE', dial: '51', name: 'Pérou' },
    { code: 'PH', dial: '63', name: 'Philippines' },
    { code: 'PL', dial: '48', name: 'Pologne' },
    { code: 'PT', dial: '351', name: 'Portugal' },
    { code: 'QA', dial: '974', name: 'Qatar' },
    { code: 'RO', dial: '40', name: 'Roumanie' },
    { code: 'GB', dial: '44', name: 'Royaume-Uni' },
    { code: 'RU', dial: '7', name: 'Russie' },
    { code: 'RW', dial: '250', name: 'Rwanda' },
    { code: 'KN', dial: '1869', name: 'Saint-Christophe-et-Niévès' },
    { code: 'SM', dial: '378', name: 'Saint-Marin' },
    { code: 'VC', dial: '1784', name: 'Saint-Vincent-et-les-Grenadines' },
    { code: 'LC', dial: '1758', name: 'Sainte-Lucie' },
    { code: 'SV', dial: '503', name: 'Salvador' },
    { code: 'WS', dial: '685', name: 'Samoa' },
    { code: 'ST', dial: '239', name: 'Sao Tomé-et-Principe' },
    { code: 'SN', dial: '221', name: 'Sénégal' },
    { code: 'RS', dial: '381', name: 'Serbie' },
    { code: 'SC', dial: '248', name: 'Seychelles' },
    { code: 'SL', dial: '232', name: 'Sierra Leone' },
    { code: 'SG', dial: '65', name: 'Singapour' },
    { code: 'SK', dial: '421', name: 'Slovaquie' },
    { code: 'SI', dial: '386', name: 'Slovénie' },
    { code: 'SO', dial: '252', name: 'Somalie' },
    { code: 'SD', dial: '249', name: 'Soudan' },
    { code: 'SS', dial: '211', name: 'Soudan du Sud' },
    { code: 'LK', dial: '94', name: 'Sri Lanka' },
    { code: 'SE', dial: '46', name: 'Suède' },
    { code: 'CH', dial: '41', name: 'Suisse' },
    { code: 'SR', dial: '597', name: 'Suriname' },
    { code: 'SY', dial: '963', name: 'Syrie' },
    { code: 'TJ', dial: '992', name: 'Tadjikistan' },
    { code: 'TW', dial: '886', name: 'Taïwan' },
    { code: 'TZ', dial: '255', name: 'Tanzanie' },
    { code: 'TD', dial: '235', name: 'Tchad' },
    { code: 'CZ', dial: '420', name: 'Tchéquie' },
    { code: 'TH', dial: '66', name: 'Thaïlande' },
    { code: 'TL', dial: '670', name: 'Timor oriental' },
    { code: 'TG', dial: '228', name: 'Togo' },
    { code: 'TO', dial: '676', name: 'Tonga' },
    { code: 'TT', dial: '1868', name: 'Trinité-et-Tobago' },
    { code: 'TN', dial: '216', name: 'Tunisie' },
    { code: 'TM', dial: '993', name: 'Turkménistan' },
    { code: 'TR', dial: '90', name: 'Turquie' },
    { code: 'TV', dial: '688', name: 'Tuvalu' },
    { code: 'UA', dial: '380', name: 'Ukraine' },
    { code: 'UY', dial: '598', name: 'Uruguay' },
    { code: 'VU', dial: '678', name: 'Vanuatu' },
    { code: 'VA', dial: '39', name: 'Vatican' },
    { code: 'VE', dial: '58', name: 'Venezuela' },
    { code: 'VN', dial: '84', name: 'Viêt Nam' },
    { code: 'YE', dial: '967', name: 'Yémen' },
    { code: 'ZM', dial: '260', name: 'Zambie' },
    { code: 'ZW', dial: '263', name: 'Zimbabwe' },
];
const DEFAULT_COUNTRY = 'BJ';
const BENIN_PREFIX = '01';

const getCountry = (code) => COUNTRIES.find((c) => c.code === code) || COUNTRIES[0];

function cleanLocal(country, value) {
    let digits = value.replace(/\D/g, '');
    if (country === 'BJ') {
        if (digits.length > 8 && digits.startsWith('22901')) digits = digits.slice(5);
        else if (digits.length > 8 && digits.startsWith('01')) digits = digits.slice(2);
        return digits.slice(0, 8);
    }
    return digits.replace(/^0+/, '').slice(0, 12);
}

function buildE164(country, localDigits) {
    if (!localDigits) return '';
    const { dial } = getCountry(country);
    return country === 'BJ'
        ? `+${dial}${BENIN_PREFIX}${localDigits}`
        : `+${dial}${localDigits}`;
}

/* ============================================================
 *  YES/NO
 * ============================================================ */
function YesNo({ name, value, onChange, error, describedBy }) {
    return (
        <div className="yesno" role="radiogroup" aria-describedby={describedBy}>
            {[
                { v: 'oui', label: 'Oui' },
                { v: 'non', label: 'Non' },
            ].map((o) => (
                <label
                    key={o.v}
                    className={`yesno__opt ${value === o.v ? 'is-on' : ''} ${error ? 'has-error' : ''}`}
                >
                    <input
                        type="radio"
                        name={name}
                        value={o.v}
                        checked={value === o.v}
                        onChange={() => onChange(name, o.v)}
                    />
                    <span>{o.label}</span>
                </label>
            ))}
        </div>
    );
}

/* ============================================================
 *  FORMULAIRE
 * ============================================================ */
const INITIAL = {
    nom: '',
    prenom: '',
    institution: '',
    telephone: '',
    certifie: '',
    droitImage: '',
};

function RegistrationForm({ onSuccess }) {
    const [values, setValues] = useState(INITIAL);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
    const [phoneLocal, setPhoneLocal] = useState('');

    const setField = (name, value) => {
        setValues((v) => ({ ...v, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    };

    const handleChange = (e) => setField(e.target.name, e.target.value);

    const handlePhoneInput = (e) => {
        const raw = e.target.value;
        const local = cleanLocal(phoneCountry, raw);
        setPhoneLocal(local);

        const e164 = buildE164(phoneCountry, local);
        setValues((v) => ({ ...v, telephone: e164 }));
        setErrors((er) => ({ ...er, telephone: undefined }));
    };

    const handleCountryChange = (e) => {
        const newCountry = e.target.value;
        const local = cleanLocal(newCountry, phoneLocal);
        setPhoneCountry(newCountry);
        setPhoneLocal(local);

        const e164 = buildE164(newCountry, local);
        setValues((v) => ({ ...v, telephone: e164 }));
        setErrors((er) => ({ ...er, telephone: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!values.nom.trim()) e.nom = 'Indiquez votre nom.';
        if (!values.prenom.trim()) e.prenom = 'Indiquez votre prénom.';
        if (!values.institution.trim()) e.institution = 'Indiquez votre institution ou entreprise.';

        const localDigits = phoneLocal.replace(/\D/g, '');
        if (!values.telephone) {
            e.telephone = 'Indiquez votre numéro de téléphone.';
        } else if (phoneCountry === 'BJ' && localDigits.length !== 8) {
            e.telephone = 'Entrez un numéro béninois valide (8 chiffres après le 01).';
        } else if (phoneCountry !== 'BJ' && localDigits.length < 6) {
            e.telephone = 'Entrez un numéro de téléphone valide.';
        }

        if (!values.certifie) e.certifie = 'Choisissez Oui ou Non.';
        if (!values.droitImage) e.droitImage = 'Choisissez Oui ou Non.';
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        setServerError('');
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length > 0) {
            const first = Object.keys(e)[0];
            document.querySelector(`[name="${first}"]`)?.focus();
            return;
        }

        setLoading(true);
        try {
            await submitRegistration({
                nom: values.nom.trim(),
                prenom: values.prenom.trim(),
                institution: values.institution.trim(),
                telephone: values.telephone,
                certifie: values.certifie === 'oui',
                droit_image: values.droitImage === 'oui',
            });
            onSuccess(values.prenom.trim());
        } catch (err) {
            setServerError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const Flag = flags[phoneCountry];
    const country = getCountry(phoneCountry);

    return (
        <form className="form" onSubmit={handleSubmit} noValidate>
            <h2 className="form__title">Je m’inscris</h2>
            <p className="form__intro">Quelques informations et votre place est réservée.</p>

            <div className="row">
                <div className="field">
                    <label htmlFor="nom">Nom</label>
                    <input
                        id="nom"
                        name="nom"
                        type="text"
                        autoComplete="family-name"
                        value={values.nom}
                        onChange={handleChange}
                        aria-invalid={!!errors.nom}
                        aria-describedby={errors.nom ? 'err-nom' : undefined}
                    />
                    {errors.nom && <p className="error" id="err-nom">{errors.nom}</p>}
                </div>
                <div className="field">
                    <label htmlFor="prenom">Prénom</label>
                    <input
                        id="prenom"
                        name="prenom"
                        type="text"
                        autoComplete="given-name"
                        value={values.prenom}
                        onChange={handleChange}
                        aria-invalid={!!errors.prenom}
                        aria-describedby={errors.prenom ? 'err-prenom' : undefined}
                    />
                    {errors.prenom && <p className="error" id="err-prenom">{errors.prenom}</p>}
                </div>
            </div>

            <div className="field">
                <label htmlFor="institution">Institution ou entreprise</label>
                <input
                    id="institution"
                    name="institution"
                    type="text"
                    autoComplete="organization"
                    value={values.institution}
                    onChange={handleChange}
                    aria-invalid={!!errors.institution}
                    aria-describedby={errors.institution ? 'err-institution' : undefined}
                />
                {errors.institution && (
                    <p className="error" id="err-institution">{errors.institution}</p>
                )}
            </div>

            {/* ---------- CHAMP TÉLÉPHONE ---------- */}
            <div className="field">
                <label htmlFor="telephone">Numéro de téléphone</label>

                <div className={`phone-field ${errors.telephone ? 'has-error' : ''}`}>
                    <div className="phone-country">
                        {Flag ? <Flag title={country.name} /> : null}
                        <span className="phone-country__arrow" aria-hidden="true">▾</span>
                        <select
                            aria-label="Pays"
                            value={phoneCountry}
                            onChange={handleCountryChange}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.code} value={c.code}>
                                    {c.name} (+{c.dial})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="phone-number">
                        <span className="phone-number__prefix" aria-hidden="true">
                            +{country.dial}
                            {phoneCountry === 'BJ' && ` ${BENIN_PREFIX}`}
                        </span>
                        <input
                            id="telephone"
                            name="telephone"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel-national"
                            value={phoneLocal}
                            onChange={handlePhoneInput}
                            placeholder={phoneCountry === 'BJ' ? 'XX XX XX XX' : '00 00 00 00'}
                            aria-invalid={!!errors.telephone}
                            aria-describedby={errors.telephone ? 'err-telephone' : undefined}
                        />
                    </div>
                </div>

                {errors.telephone && (
                    <p className="error" id="err-telephone">{errors.telephone}</p>
                )}
            </div>

            <fieldset className="field">
                <legend>Êtes-vous certifié(e) ?</legend>
                <YesNo
                    name="certifie"
                    value={values.certifie}
                    onChange={setField}
                    error={errors.certifie}
                    describedBy={errors.certifie ? 'err-certifie' : undefined}
                />
                {errors.certifie && (
                    <p className="error" id="err-certifie">{errors.certifie}</p>
                )}
            </fieldset>

            <fieldset className="field">
                <legend>Droit à l’image</legend>
                <p className="hint" id="hint-image">
                    Autorisez-vous les organisateurs à utiliser votre image (photos et vidéos prises
                    pendant l’événement) pour la promotion de la soirée ?
                </p>
                <YesNo
                    name="droitImage"
                    value={values.droitImage}
                    onChange={setField}
                    error={errors.droitImage}
                    describedBy="hint-image"
                />
                {errors.droitImage && (
                    <p className="error" id="err-droitImage">{errors.droitImage}</p>
                )}
            </fieldset>

            {serverError && (
                <p className="error error--server" role="alert">{serverError}</p>
            )}

            <button type="submit" className="btn" disabled={loading}>
                {loading ? 'Envoi en cours…' : 'Confirmer mon inscription'}
            </button>
        </form>
    );
}

/* ============================================================
 *  PAGE COMPLÈTE
 * ============================================================ */
export default function RegistrationPage() {
    const [successPrenom, setSuccessPrenom] = useState(null);

    const handleSuccess = (prenom) => setSuccessPrenom(prenom);
    const handleReset = () => setSuccessPrenom(null);

    return (
        <div className="afterwork-page">
            <Background />
            <main className="page">
                <section className="hero">
                    <h1 className="hero__title">{EVENT.eventName}</h1>
                    <p className="hero__tagline">{EVENT.eventTagline}</p>
                    <ul className="hero__infos">
                        <li>
                            <FaCalendarAlt className="hero__icon" aria-hidden="true" />
                            <span>{EVENT.eventDate}</span>
                        </li>
                        <li>
                            <FaClock className="hero__icon" aria-hidden="true" />
                            <span>{EVENT.eventTime}</span>
                        </li>
                        <li>
                            <FaMapMarkerAlt className="hero__icon" aria-hidden="true" />
                            <span>{EVENT.eventPlace}</span>
                        </li>
                    </ul>
                </section>

                <section className="card" aria-live="polite">
                    {successPrenom ? (
                        <SuccessMessage prenom={successPrenom} onReset={handleReset} />
                    ) : (
                        <RegistrationForm onSuccess={handleSuccess} />
                    )}
                </section>
            </main>
        </div>
    );
}